import {
  AccountingDashboardAccommodationDailyRow,
  AccountingDashboardRevenueGrowthDailyRow,
} from '@/renderer/types/accounting';
import { Button } from '@components/common';
import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildRevenueVsOccupancyChartData,
  formatCompactCurrency,
  formatCurrencyValue,
} from './accountingDashboard';

type PerformanceMode = 'revenue-vs-expense' | 'revenue-vs-occupancy';
type ChartPoint = {
  label: string;
  revenue: number;
  expense?: number;
  occupancy?: number;
};

interface AccountingPerformanceCoreProps {
  revenueRows: AccountingDashboardRevenueGrowthDailyRow[];
  accommodationRows: AccountingDashboardAccommodationDailyRow[];
  totalExpenses: number;
  averageDailyRate: number;
  averageRevPar: number;
  averageTicketSize: number;
  averageBookingValue: number;
}

export function AccountingPerformanceCore({
  revenueRows,
  accommodationRows,
  totalExpenses,
  averageDailyRate,
  averageRevPar,
  averageTicketSize,
  averageBookingValue,
}: AccountingPerformanceCoreProps) {
  const [mode, setMode] = useState<PerformanceMode>('revenue-vs-expense');
  const revenueExpenseData: ChartPoint[] = useMemo(
    () =>
      revenueRows.map((row) => ({
        label: new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
        }).format(new Date(row.metric_date)),
        revenue: row.total_revenue_amount,
        expense:
          revenueRows.length > 0 ? totalExpenses / revenueRows.length : 0,
      })),
    [revenueRows, totalExpenses],
  );
  const revenueOccupancyData: ChartPoint[] = useMemo(
    () => buildRevenueVsOccupancyChartData(revenueRows, accommodationRows),
    [revenueRows, accommodationRows],
  );
  const chartData: ChartPoint[] =
    mode === 'revenue-vs-expense' ? revenueExpenseData : revenueOccupancyData;

  return (
    <div className="space-y-3">
      <div className="rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Performance core
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Switch between profit pressure and room performance.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={mode === 'revenue-vs-expense' ? 'primary' : 'outline'}
              onClick={() => setMode('revenue-vs-expense')}
            >
              Revenue vs expenses
            </Button>
            <Button
              size="sm"
              variant={mode === 'revenue-vs-occupancy' ? 'primary' : 'outline'}
              onClick={() => setMode('revenue-vs-occupancy')}
            >
              Revenue vs occupancy
            </Button>
          </div>
        </div>

        <div className="mt-3 h-[220px]">
          {!chartData.length ? (
            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
              No performance data is available for this period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: -10, bottom: 0 }}
              >
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                />
                <YAxis
                  yAxisId="money"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                  tickFormatter={(value) =>
                    formatCompactCurrency(Number(value))
                  }
                />
                {mode === 'revenue-vs-occupancy' && (
                  <YAxis
                    yAxisId="occupancy"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    tickFormatter={(value) => `${Number(value).toFixed(0)}%`}
                  />
                )}
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Occupancy') {
                      return [`${Number(value ?? 0).toFixed(1)}%`, name];
                    }

                    return [formatCurrencyValue(Number(value ?? 0)), name];
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                  contentStyle={{
                    borderRadius: 16,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                  }}
                />
                <Legend />
                <Line
                  yAxisId="money"
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--color-pos-primary, #16a34a)"
                  strokeWidth={3}
                  dot={false}
                  name="Revenue"
                />
                {mode === 'revenue-vs-expense' ? (
                  <Line
                    yAxisId="money"
                    type="monotone"
                    dataKey="expense"
                    stroke="#475569"
                    strokeWidth={2}
                    dot={false}
                    name="Expenses"
                  />
                ) : (
                  <Line
                    yAxisId="occupancy"
                    type="monotone"
                    dataKey="occupancy"
                    stroke="var(--color-pos-primary, #16a34a)"
                    strokeWidth={2}
                    dot={false}
                    name="Occupancy"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          {
            label: 'Average Daily Rate',
            value: averageDailyRate,
            helper: 'Avg room revenue per occupied room-night.',
          },
          {
            label: 'RevPAR',
            value: averageRevPar,
            helper: 'Revenue per available room in range.',
          },
          {
            label: 'Average Ticket Size',
            value: averageTicketSize,
            helper: 'Avg recognized sales per settled order.',
          },
          {
            label: 'Average Booking Value',
            value: averageBookingValue,
            helper: 'Avg room revenue per active booking.',
          },
        ].map((item) => (
          <div
            key={item.label}
            className="rounded-[20px] border border-gray-200 bg-white p-3.5 shadow-sm"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {item.label}
            </p>
            <p className="mt-1.5 text-xl font-semibold tracking-tight text-slate-950">
              {item.value > 0 ? formatCompactCurrency(item.value) : '—'}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">{item.helper}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
